/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { Injectable, Provider } from '@angular/core';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatDateFormats } from '@angular/material/core';
import { DateFnsAdapter } from '@angular/material-date-fns-adapter';
import { format, isValid, parse, parseISO } from 'date-fns';
import { th } from 'date-fns/locale';
import { BUDDHIST_ERA_OFFSET, toBuddhistYear, withBuddhistYear } from '../pipes/thai-date.pipe';

/**
 * A typed year at or above this is read as Buddhist Era. The two eras never
 * overlap for a document: CE 2400 is three centuries away, and BE 2400 was 1857.
 */
export const BUDDHIST_YEAR_THRESHOLD = 2400;

/** Years below this are a typing slip (`20/5/67` reads as the year 67), not a date. */
const EARLIEST_YEAR = 1000;

/** Day first, as Thai readers write it; the separators people actually type. */
export const ACA_THAI_DATE_FORMATS: MatDateFormats = {
  parse: { dateInput: ['d/M/yyyy', 'd-M-yyyy', 'd.M.yyyy'] },
  display: {
    dateInput: 'dd/MM/yyyy',
    monthYearLabel: 'LLL yyyy',
    dateA11yLabel: 'd MMMM yyyy',
    monthYearA11yLabel: 'LLLL yyyy'
  }
};

/**
 * Rewrites every four-digit Buddhist Era year in typed text as Common Era.
 *
 * The text is converted before it is parsed, not the parsed date after: a date
 * parser would reject 29/02/2567, since 2567 read as a Common Era year is not a
 * leap year, although 2024 is.
 */
export function fromBuddhistYears(text: string): string {
  return text.replace(/(^|\D)(\d{4})(?=\D|$)/g, (match, before: string, digits: string) => {
    const year = Number(digits);
    return year >= BUDDHIST_YEAR_THRESHOLD ? `${before}${year - BUDDHIST_ERA_OFFSET}` : match;
  });
}

/**
 * Material date adapter that shows and accepts **Buddhist Era** years, while the
 * `Date` values it hands back stay ordinary Common Era dates.
 *
 * Only what a person reads or types changes: `format` and `getYearName` print
 * the year plus 543, and `parse` accepts either era. `getYear`, date arithmetic
 * and `toIso8601` are untouched, so the calendar's paging and any value sent to
 * the repository are unaffected.
 */
@Injectable()
export class BuddhistDateAdapter extends DateFnsAdapter {
  override format(date: Date, displayFormat: string): string {
    const formatted = super.format(date, displayFormat);
    return hasYearToken(displayFormat) ? withBuddhistYear(formatted, this.getYear(date)) : formatted;
  }

  override getYearName(date: Date): string {
    return String(toBuddhistYear(this.getYear(date)));
  }

  override parse(value: unknown, parseFormat: string | string[]): Date | null {
    if (typeof value !== 'string') {
      return super.parse(value, parseFormat);
    }

    const text = fromBuddhistYears(value.trim());
    if (!text) {
      return null;
    }

    // a full ISO date only — date-fns would otherwise read a bare "2567" as a year
    if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
      return this.validOrInvalid(parseISO(text));
    }

    for (const pattern of Array.isArray(parseFormat) ? parseFormat : [parseFormat]) {
      const date = parse(text, pattern, new Date(), { locale: this.locale });
      if (isValid(date)) {
        return this.validOrInvalid(date);
      }
    }
    return this.invalid();
  }

  private validOrInvalid(date: Date): Date {
    return isValid(date) && this.getYear(date) >= EARLIEST_YEAR ? date : this.invalid();
  }
}

/** True when a date-fns pattern prints a year; quoted literal text is ignored. */
function hasYearToken(pattern: string): boolean {
  return /[yYu]/.test(pattern.replace(/'[^']*'/g, ''));
}

/**
 * Providers for a Material date picker that reads and writes Buddhist Era
 * years with Thai month names. Provide them on the component that hosts the
 * picker, not application-wide: ADF's own pickers bring their own adapter and
 * format values for the repository through it.
 */
export function provideBuddhistDateAdapter(): Provider[] {
  return [
    { provide: MAT_DATE_LOCALE, useValue: th },
    { provide: DateAdapter, useClass: BuddhistDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: ACA_THAI_DATE_FORMATS }
  ];
}

/**
 * Reads the `yyyy-MM-dd` value a native date input used to produce as a local
 * date, or null. A longer ISO string is cut to its date part, so a timestamp
 * lands on the day it names rather than shifting across midnight in UTC.
 */
export function isoDateToDate(value: string | null | undefined): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? '');
  if (!match) {
    return null;
  }
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return isValid(date) ? date : null;
}

/** The inverse of `isoDateToDate`: a local date as `yyyy-MM-dd`, or '' for none. */
export function dateToIsoDate(date: Date | null | undefined): string {
  return date && isValid(date) ? format(date, 'yyyy-MM-dd') : '';
}
